import type { CvtStatus } from '@packages/models/api/cvt';
import type { Dispatch } from 'react';

import { action, on, props, reducer, union } from 'ts-action';

import { isUploadedCvtDiagnosticFinished } from './qmr-cvt-diagnostic.utils';

/** The diagnostic uploaded from each report, kept while it processes and the user leaves the report and comes back. */
export type CvtDiagnosticUploadsState = {
    [qmrId: string]: UploadedCvtDiagnostic;
};

/**
 * A diagnostic uploaded from the report, from its upload until it's attached or cleared. A No Result test stays after
 * it's attached, with its result, so the section can show its alert.
 */
export type UploadedCvtDiagnostic = {
    /** The id the upload started, which the report's `cvtDiagnosticsJobId` takes once it's attached. */
    diagnosticId: string;

    /** The report the diagnostic was uploaded from. Only that report shows, polls or attaches it. */
    qmrId: string;

    /** When the file was uploaded. The poll's time limit counts from it. */
    uploadedAt: Date;

    /** SBR's detail on its error type, which can contain `<BR>` line breaks. */
    errorDetail?: string;

    /** SBR's error type when it sends one, such as `NoResult` for a test completed without symptoms. */
    errorType?: string;

    /** Whether it couldn't be attached: a check or the attach failed, or it was still processing at the time limit. */
    hasAttachError?: boolean;

    /** The status it finished with, as the backend sends it. Absent while it processes. */
    status?: CvtStatus;

    /** The backend's message on why the test failed, as it sends it. */
    stsErrorResponse?: string;
};

export const cvtDiagnosticUploadsActions = {
    clearFinishedUploadedDiagnostic: action('CLEAR_FINISHED_UPLOADED_CVT_DIAGNOSTIC', props<{ qmrId: string }>()),
    clearUploadedDiagnostic: action('CLEAR_UPLOADED_CVT_DIAGNOSTIC', props<{ qmrId: string }>()),
    setUploadedDiagnostic: action(
        'SET_UPLOADED_CVT_DIAGNOSTIC',
        props<{ uploadedDiagnostic: UploadedCvtDiagnostic }>()
    ),
};

const actionsUnion = union(...Object.values(cvtDiagnosticUploadsActions));
type CvtDiagnosticUploadsActions = typeof actionsUnion.actions;

export type CvtDiagnosticUploadsActionDispatcher = Dispatch<CvtDiagnosticUploadsActions>;

export const initialState: CvtDiagnosticUploadsState = {};

export const cvtDiagnosticUploadsReducer = reducer(
    initialState,
    on(cvtDiagnosticUploadsActions.clearFinishedUploadedDiagnostic, (state, { qmrId }) => {
        if (!isUploadedCvtDiagnosticFinished(state[qmrId])) {
            return state;
        }

        const updatedState = { ...state };

        delete updatedState[qmrId];

        return updatedState;
    }),
    on(cvtDiagnosticUploadsActions.clearUploadedDiagnostic, (state, { qmrId }) => {
        const updatedState = { ...state };

        delete updatedState[qmrId];

        return updatedState;
    }),
    on(cvtDiagnosticUploadsActions.setUploadedDiagnostic, (state, { uploadedDiagnostic }) => {
        return {
            ...state,
            [uploadedDiagnostic.qmrId]: uploadedDiagnostic,
        };
    })
);
